/**
 * Iter-183 — Custom Inspection Answers.
 *
 * Company admins add "additional questions" to the inspection form. The
 * inspector's answers are stored on the inspection as
 * `custom_answers: { [questionId]: string | { value, note, photos[] } }`
 * next to a `custom_questions_snapshot` of the questions as they were at
 * the time. This helper flattens both shapes into display rows shared by
 * the inspection detail screen and the PDF renderers.
 */
export interface CustomAnswerRow {
  id: string;
  label: string;
  type: string;
  value: string;
  note: string;
  photos: string[];
  /** No / Fail */
  negative: boolean;
  /** Yes / Pass */
  positive: boolean;
}

export const normaliseCustomAnswers = (inspection: any): CustomAnswerRow[] => {
  const answers = inspection?.custom_answers;
  if (!answers || typeof answers !== 'object') return [];
  const snapshot: any[] = Array.isArray(inspection?.custom_questions_snapshot) ? inspection.custom_questions_snapshot : [];
  const byId = new Map<string, any>(snapshot.map((q) => [String(q.id), q]));
  const ids = snapshot.length ? snapshot.map((q) => String(q.id)) : Object.keys(answers);
  // Answers for questions that were added after the snapshot still show.
  for (const k of Object.keys(answers)) if (!ids.includes(k)) ids.push(k);

  const rows: CustomAnswerRow[] = [];
  for (const id of ids) {
    const raw = answers[id];
    const q = byId.get(id);
    let value = '';
    let note = '';
    let photos: string[] = [];
    if (raw && typeof raw === 'object') {
      value = raw.value != null ? String(raw.value) : '';
      note = raw.note ? String(raw.note) : '';
      photos = Array.isArray(raw.photos) ? raw.photos.filter((p: any) => typeof p === 'string' && p) : [];
    } else if (raw != null) {
      value = String(raw);
    }
    if (!value.trim() && !note.trim() && photos.length === 0) continue;
    const v = value.trim().toLowerCase();
    rows.push({
      id,
      label: q?.label || 'Additional question',
      type: q?.type || (photos.length && !value ? 'photo' : 'text'),
      value: value.trim(),
      note: note.trim(),
      photos,
      negative: v === 'no' || v === 'fail',
      positive: v === 'yes' || v === 'pass',
    });
  }
  return rows;
};
